/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

// React Imports
import React from 'react';
import { PropTypes } from 'prop-types';
import { CardTitle, Col, Row } from 'reactstrap';
import ReactDOMServer from 'react-dom/server';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';
import { FIVE_MINUTES } from '../../../../../../constants/general';

// External Libraries
import moment from 'moment';

// Custom Imports
import {
   AlignedIconContainer,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   StandardContainer,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
   BottomMargin,
   CenteredMsgShowing,
} from '../../../../../../styledComponents/styledComponents';
import { AlertSubtitle, ContentWrapper } from './AlertWidgetStyles';
import TimelineSection from './TimelineSection';
import { ALERT_MAPPER } from './AlertDetailsMapper';
import AlertDetailsPanel from './AlertDetailsPanel';
import { CsvDataExportButton } from '../../../../../common';
import BlockSpinner from '../../../../../common/BlockSpinner/BlockSpinner';
import { useAlertDetails } from './useAlertDetails';
import {
   CHRONOLOGICAL_DATE_TIME_FORMAT,
   CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED,
   CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT,
} from '../../../constants';

function InactiveIngestWidget({ selectedAlert, streamName, height }) {
   const codes = selectedAlert.codes;
   const ALERT_CODE = codes.sort((a, b) => b - a)[0]; // get highest code
   const { title, alertInfo } = ALERT_MAPPER[ALERT_CODE];

   const { details, isFetching: detailsFetching } = useAlertDetails(selectedAlert, streamName);

   if (detailsFetching) {
      return <BlockSpinner paddingTopBottomPx={50} />;
   }

   const connections = details?.custom?.connections || [];
   const alertStart = moment.utc(selectedAlert.first);
   const alertEnd = moment.utc(selectedAlert.last);
   const rangeStart = alertStart.clone().subtract(FIVE_MINUTES, 'seconds').valueOf(); // in MS
   const rangeEnd = alertEnd.clone().add(FIVE_MINUTES, 'seconds').valueOf(); // in MS
   const totalDuration = rangeEnd - rangeStart;
   const alertStartPercent = ((alertStart.valueOf() - rangeStart) / totalDuration) * 100;
   const alertEndPercent = ((alertEnd.valueOf() - rangeStart) / totalDuration) * 100;

   // prettier-ignore
   const CSV_FILE_NAME = `${moment.utc().format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_alert_ii_id-${selectedAlert.id}_from-${moment.utc(selectedAlert.first).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_to-${moment.utc(selectedAlert.last).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}`;
   const csvData = connections.flatMap((conn) =>
      conn.blocks.map((block) => ({
         connectionId: conn.connectionId,
         status: block.isActive ? 'Active' : 'Inactive',
         from: moment.utc(block.startTimestamp * 1000).format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         to: moment.utc(block.endTimestamp * 1000).format(CHRONOLOGICAL_DATE_TIME_FORMAT),
      })),
   );

   return (
      <>
         <PaddingContainer>
            <WidgetContainer $margin={'0px 0px 0px 0px'}>
               <WidgetHeaderContainer>
                  <HeightSetContainer $heightInPx={23}>
                     <TwoSideFlexContainer>
                        <SimpleFlexRow>
                           {details.message === 'SUCCESS' && connections.length > 0 && (
                              <SimpleFlexItem>
                                 <CsvDataExportButton jsonData={csvData} filename={CSV_FILE_NAME} options={{}} />
                              </SimpleFlexItem>
                           )}
                        </SimpleFlexRow>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <AlignedIconContainer
                                 data-tooltip-id='II'
                                 data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{alertInfo}</div>)}
                                 className='bi bi-info-circle ml-1'
                              ></AlignedIconContainer>
                              <ReactTooltip
                                 id='II'
                                 place={'top'}
                                 className={'reduced-line-height bring-on-top'}
                                 backgroundColor={'rgba(0,0,0,0.9)'}
                                 textColor={'rgb(240,240,240)'}
                                 style={{ textAlign: 'center' }}
                                 clickable
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                     </TwoSideFlexContainer>
                  </HeightSetContainer>
               </WidgetHeaderContainer>
               <WidgetBodyContainer>
                  <BottomMargin pxSize={20}>
                     <Row>
                        <Col>
                           <CardTitle className='mb-0'>{title}</CardTitle>
                           <AlertSubtitle>{`From ${moment
                              .utc(selectedAlert.first)
                              .format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)} to ${moment
                              .utc(selectedAlert.last)
                              .format(CHRONOLOGICAL_DATE_TIME_SHORT_FORMAT)} \u00A0\u00A0( UTC time )`}</AlertSubtitle>
                        </Col>
                     </Row>
                  </BottomMargin>
                  {details.message === 'SUCCESS' ? (
                     <ContentWrapper>
                        <StandardContainer $paddingPx={20} $paddingTopPx={0}>
                           {connections.length > 0 && totalDuration > 0 && (
                              <TimelineSection
                                 connections={connections}
                                 rangeStart={rangeStart}
                                 totalDuration={totalDuration}
                                 alertStart={alertStart}
                                 alertEnd={alertEnd}
                                 alertStartPercent={alertStartPercent}
                                 alertEndPercent={alertEndPercent}
                              />
                           )}
                        </StandardContainer>
                        <AlertDetailsPanel selectedAlert={selectedAlert} />
                     </ContentWrapper>
                  ) : (
                     <ContentWrapper>
                        <CenteredMsgShowing height={height} marginTopPx={20}>
                           {'No data available'}
                           <br />
                           {details.message}
                        </CenteredMsgShowing>
                     </ContentWrapper>
                  )}
               </WidgetBodyContainer>
            </WidgetContainer>
         </PaddingContainer>
      </>
   );
}

InactiveIngestWidget.propTypes = {
   selectedAlert: PropTypes.object,
   streamName: PropTypes.string,
   height: PropTypes.number,
};

InactiveIngestWidget.defaultProps = {
   height: 300,
};

export default InactiveIngestWidget;
